/**
 * Customer ticket board API (Linear JCD-20/JCD-21).
 *
 * Distinct from SupportDrawer's /api/support/ticket (singular — files a NEW
 * ticket): this reads the site's own JCD tickets back as a board and lets
 * the site follow status + comment. The licensing server is the source of
 * truth; `offline: true` on the board means the box served its local
 * fallback because the licensing server was unreachable, not that there is
 * no data — the caller still renders the tickets, just with a notice.
 *
 * Same idiom as payables.ts (this module is the newest sibling of that
 * pattern, not a variant of it): a private csrf()/req()/writeInit() trio
 * rather than a shared client, so this file has no import-time coupling to
 * any other api/*.ts module.
 */

export type SupportTicketState = 'received' | 'in_progress' | 'done' | 'closed';
export type SupportTicketKind = 'bug' | 'suggestion' | 'question' | 'report';

export interface SupportBoardMessage {
  id: string;
  from: 'customer' | 'team';
  author: string;
  body: string;
  created_at: string;
}

export interface SupportBoardTicket {
  identifier: string;
  title: string;
  kind: SupportTicketKind;
  state: SupportTicketState;
  reporter: string;
  /** True when the current user filed this ticket (vs. a teammate at the
   *  same site) — reserved for a future "mine only" filter; not rendered yet. */
  mine: boolean;
  created_at: string;
  updated_at: string;
  description: string;
  /** Unread message count for the current user, for the board pill + badge. */
  unread: number;
  messages: SupportBoardMessage[];
}

export interface SupportBoard {
  tickets: SupportBoardTicket[];
  unread_total: number;
  synced_at: string;
  /** True when the licensing server was unreachable and this is the box's
   *  local fallback — state/messages may be stale. Render a notice, not an error. */
  offline: boolean;
}

/**
 * The cookie is `pacebi_csrf` (internal/auth/csrf.go), NOT `csrf_token` —
 * same name every other write-capable api/*.ts client reads.
 */
function csrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)pacebi_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(detail.trim() || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function writeInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
    body: JSON.stringify(body),
  };
}

export function fetchSupportBoard(): Promise<SupportBoard> {
  return req<SupportBoard>('/api/support/tickets');
}

export function postSupportComment(identifier: string, body: string): Promise<{ id: string; created_at: string }> {
  return req(`/api/support/tickets/${encodeURIComponent(identifier)}/comment`, writeInit('POST', { body }));
}

export async function ackSupportTickets(): Promise<void> {
  await req<void>('/api/support/tickets/ack', writeInit('POST', {}));
}

export function fetchSupportUnread(): Promise<{ count: number }> {
  return req<{ count: number }>('/api/support/unread');
}
